import React from 'react';
import cx from 'classnames';

import useWinkBreakpoints from '@mc/hooks/useWinkBreakpoints';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import ClusterLayout from '../ClusterLayout';
import ComboButton from '../ComboButton';
import Heading from '../Heading';
import Text from '../Text';
import StackLayout from '../StackLayout';
import { WizardBreadcrumbs } from '../Wizard';
import ContentArea from '../ContentArea';
import IconButton from '../IconButton';
import Button from '../Button';
import { ActionListItem } from '../ActionList';
import Tooltip from '../Tooltip';
import { FreddieIcon } from './components/FreddieIcon';
import stylesheet from './FocusedStatePageHeader.css';

import HamburgerMenu from './components/HamburgerMenu/HamburgerMenu';
import type { FocusedStatePageHeaderProps } from './interfaces';
import { TranslateFocusedStatePageHeader } from './TranslateFocusedStatePageHeader';

/**
 * `FocusedStatePageHeader` should always be wrapped by `Page` to create an isolated stacking context which does not interfere with other z-indexes.
 */
const FocusedStatePageHeader = React.forwardRef<
  HTMLElement,
  FocusedStatePageHeaderProps
>(function FocusedStatePageHeader(
  {
    title,
    actionTitle,
    badge,
    actions,
    wizardSteps = null,
    children,
    navigationItems = [],
    className,
    removeBackNav = false,
  },
  forwardedRef,
) {
  const isUsingWizardBreadcrumbs =
    wizardSteps &&
    (wizardSteps as React.ReactElement).type === WizardBreadcrumbs;
  const { isPhone, isTablet } = useWinkBreakpoints();
  const translations = TranslateFocusedStatePageHeader();

  const isFocusedStatePageHeaderOn = mcdsFlagCheck(
    'xp_mcds_redesign_focused_state_page_header',
  );

  // Process navigation items with validation based on removeBackNav
  const processedNavigationItems = React.useMemo(() => {
    const items = [];

    if (removeBackNav) {
      // When removeBackNav is true, consumer must provide 1-2 navigationItems
      // If more than 2 navigationItems provided, only use first 2
      if (navigationItems.length > 2) {
        return navigationItems.slice(0, 2);
      }
      return navigationItems;
    }
    // When removeBackNav is false, add back navigation first
    if (typeof window !== 'undefined' && window.history.length > 1) {
      const referrer = document.referrer;

      if (referrer && referrer.includes(window.location.hostname)) {
        items.push({
          label: translations.backNavigationLabel,
          uri: referrer,
        });
      }
    }

    // Design decision: If more than 1 navigationItem provided, only use the first one
    if (navigationItems.length > 0) {
      items.push(navigationItems[0]);
    }

    return items;
  }, [navigationItems, removeBackNav, translations.backNavigationLabel]);

  // Validation and processing of actions
  const processedActions = React.useMemo(() => {
    if (!actions || actions.length === 0) {
      return [];
    }

    const validatedActions = actions.slice(0, 4).map((action) => {
      const processedAction = { ...action };

      // If there is only one action and it is a cancelAction, force it to have "secondary" type
      if (actions.length === 1 && action.cancelAction) {
        processedAction.type = 'secondary';
      }

      return processedAction;
    });

    // If there are more than one actions passed in, one MUST be a primary action
    // Design decision: Auto-assign primary type to last action if none specified
    if (validatedActions.length > 1) {
      const hasPrimary = validatedActions.some(
        (action) => action.type === 'primary',
      );
      if (!hasPrimary) {
        // Auto-assign primary to the last action if none specified
        validatedActions[validatedActions.length - 1].type = 'primary';
      }
    }

    return validatedActions;
  }, [actions]);

  // If there is only one action, do not collapse to ComboButton
  const shouldUseComboButton =
    (isTablet || isPhone) && processedActions.length > 1;

  // When collapsing buttons, find the primary action for ComboButton props
  const primaryAction = processedActions.find(
    (action) => action.type === 'primary',
  );
  const nonPrimaryActions = processedActions.filter(
    (action) => action.type !== 'primary',
  );

  return (
    isFocusedStatePageHeaderOn && (
      <div className={stylesheet.container}>
        <ContentArea
          size="max"
          as="header"
          ref={forwardedRef}
          className={cx(stylesheet.header, className)}
        >
          <ClusterLayout
            justifyContent="space-between"
            nowrap
            className={stylesheet.headerContent}
          >
            <ClusterLayout
              nowrap
              className={stylesheet.titleOverflowEllipsisCluster}
              gap={2}
            >
              <ClusterLayout gap={0} nowrap>
                <HamburgerMenu navigationItems={processedNavigationItems} />
                <Tooltip
                  label={translations.homeTooltipLabel}
                  direction="bottom"
                >
                  <IconButton
                    label={translations.homeTooltipLabel}
                    icon={<FreddieIcon className={stylesheet.freddie_logo} />}
                    className={stylesheet.freddie_button}
                    href="/"
                  />
                </Tooltip>
              </ClusterLayout>
              <StackLayout
                gap={0}
                className={stylesheet.titleOverflowEllipsisStack}
              >
                {actionTitle && (
                  <Text appearance="small" className={stylesheet.actionTitle}>
                    {actionTitle}
                  </Text>
                )}
                <div
                  className={cx(
                    stylesheet.titleMenuContainer,
                    stylesheet.titleOverflowEllipsisContainer,
                  )}
                >
                  <ClusterLayout gap={2}>
                    <Heading
                      level={1}
                      appearance="heading-4"
                      className={cx(
                        stylesheet.titleOverflowEllipsisHeading,
                        stylesheet.titleContainer,
                      )}
                    >
                      {title}
                    </Heading>
                    {badge && !isPhone && (
                      <div className={stylesheet.badge}>{badge}</div>
                    )}
                  </ClusterLayout>
                </div>
              </StackLayout>
            </ClusterLayout>
            <ClusterLayout gap={2} nowrap>
              {/* Custom children content */}
              {children}

              {/* Action buttons with validation logic */}
              {shouldUseComboButton ? (
                // Condition 3: Use primary action for ComboButton props
                <ComboButton
                  label={primaryAction?.label || 'Actions'}
                  onClick={primaryAction?.action}
                >
                  {/* Include all non-primary actions in dropdown */}
                  {nonPrimaryActions.map((actionItem, index) => {
                    const handleDropdownActionClick = actionItem.action;
                    return (
                      <ActionListItem
                        label={actionItem.label}
                        key={`action-${index}`}
                        onClick={handleDropdownActionClick}
                      >
                        {actionItem.label}
                      </ActionListItem>
                    );
                  })}
                </ComboButton>
              ) : (
                <ClusterLayout gap={3} nowrap>
                  {processedActions.map((actionItem, index) => {
                    const handleActionClick = actionItem.action;
                    return (
                      <Button
                        key={`action-${index}`}
                        type={actionItem.type || 'secondary'}
                        onClick={handleActionClick}
                      >
                        {actionItem.label}
                      </Button>
                    );
                  })}
                </ClusterLayout>
              )}
            </ClusterLayout>
          </ClusterLayout>
        </ContentArea>
        {wizardSteps && (
          <div className={stylesheet.breadcrumbsContainer}>
            {isUsingWizardBreadcrumbs
              ? React.cloneElement(wizardSteps as React.ReactElement, {
                  variant: 'revised',
                })
              : wizardSteps}
          </div>
        )}
      </div>
    )
  );
});

export default FocusedStatePageHeader;
